import React, { useId } from "react"
import { Col, FormGroup, Input, Label, Row } from "reactstrap"
import { LEVELS } from "./aiTestShared"

/**
 * A plain Bootstrap checkbox. The theme draws the unchecked box almost white on white, so it gets a darker
 * border until it is ticked. `!!checked` keeps it controlled even when the value is missing, and the id is
 * unique per instance (the form is used in the "New test" modal and in the editor).
 */
const CheckboxField = ({ checked, onChange, disabled, label, hint, className = "" }) => {
  const id = useId()
  return (
    <div className={`form-check ${className}`}>
      <input
        className="form-check-input"
        type="checkbox"
        id={id}
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          cursor: disabled ? "default" : "pointer",
          ...(checked ? {} : { borderColor: "#8a8f98", borderWidth: "1.5px" }),
        }}
      />
      <label className="form-check-label" htmlFor={id} style={{ cursor: disabled ? "default" : "pointer" }}>
        {label}
      </label>
      {hint ? <div className="small text-muted">{hint}</div> : null}
    </div>
  )
}

export const emptyTestSettings = {
  title: "",
  description: "",
  curriculumId: "",
  language: "python",
  level: "Beginner",
  timeLimitMinutes: "",
  passMark: 50,
  maxAttempts: "",
  instantFeedback: true,
  shuffleQuestions: false,
  subcategoryId: "",
  sortOrder: 0,
  passRecommendKind: "",
  passRecommendCurriculumId: "",
  passRecommendTestId: "",
  failRecommendCurriculumId: "",
}

/** "course", "test" or "" — which after-pass picker the form shows. */
const passKindOf = (t) => (t.passRecommendTestId ? "test" : t.passRecommendCurriculumId ? "course" : "")

/** Form values from a test row (nulls become empty inputs). */
export const settingsFromTest = (t) => ({
  title: t.title || "",
  description: t.description || "",
  curriculumId: t.curriculumId || "",
  language: t.language || "",
  level: t.level || "",
  timeLimitMinutes: t.timeLimitMinutes ?? "",
  passMark: t.passMark ?? 50,
  maxAttempts: t.maxAttempts ?? "",
  instantFeedback: t.instantFeedback !== false,
  shuffleQuestions: !!t.shuffleQuestions,
  subcategoryId: t.subcategoryId ?? "",
  sortOrder: t.sortOrder ?? 0,
  passRecommendKind: passKindOf(t),
  passRecommendCurriculumId: t.passRecommendCurriculumId ?? "",
  passRecommendTestId: t.passRecommendTestId ?? "",
  failRecommendCurriculumId: t.failRecommendCurriculumId ?? "",
})

/**
 * Form values ready for testPayload: only the after-pass target that matches the chosen kind is kept,
 * because the API takes a course or a test after a pass, not both.
 */
export const settingsForSave = (form) => ({
  ...form,
  passRecommendCurriculumId: form.passRecommendKind === "course" ? form.passRecommendCurriculumId : "",
  passRecommendTestId: form.passRecommendKind === "test" ? form.passRecommendTestId : "",
})

const CourseOptions = ({ curricula }) =>
  curricula.map((c) => (
    <option key={c.id} value={c.id}>
      {c.title} ({c.slug}){c.isVisible ? "" : " — hidden"}
    </option>
  ))

/**
 * Settings shared by the "New test" modal and the test editor.
 * `categories` is the learning-path taxonomy (GET /admin/learning-path/category/all); `tests` lists the
 * tests that can be recommended after a pass; `testId` is this test, left out of that list.
 */
const TestSettingsFields = ({ form, setForm, curricula = [], categories = [], tests = [], testId = null, disabled = false }) => {
  const setValue = (key, value) => setForm((f) => ({ ...f, [key]: value }))
  const set = (key) => (e) => setValue(key, e.target.value)
  const subcategoryOptions = categories.flatMap((cat) =>
    (cat.subcategories || []).map((sub) => ({ id: sub.id, label: `${cat.title} → ${sub.title}` }))
  )
  const otherTests = tests.filter((t) => String(t.id) !== String(testId))
  return (
    <>
      <FormGroup>
        <Label>Title</Label>
        <Input value={form.title} onChange={set("title")} placeholder="e.g. Python Basics check" disabled={disabled} maxLength={200} />
      </FormGroup>
      <FormGroup>
        <Label>Description</Label>
        <Input
          type="textarea"
          rows={2}
          value={form.description}
          onChange={set("description")}
          placeholder="What the learner will be tested on"
          disabled={disabled}
        />
      </FormGroup>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Course</Label>
            <Input type="select" value={form.curriculumId} onChange={set("curriculumId")} disabled={disabled}>
              <option value="">Standalone (no course)</option>
              <CourseOptions curricula={curricula} />
            </Input>
            <small className="text-muted">A test on a hidden course is hidden from learners too.</small>
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup>
            <Label>Level</Label>
            <Input type="select" value={form.level} onChange={set("level")} disabled={disabled}>
              <option value="">—</option>
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup>
            <Label>Code language</Label>
            <Input value={form.language} onChange={set("language")} placeholder="python" disabled={disabled} maxLength={50} />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={8}>
          <FormGroup>
            <Label>Category</Label>
            <Input
              type="select"
              value={form.subcategoryId ?? ""}
              onChange={set("subcategoryId")}
              disabled={disabled || !subcategoryOptions.length}
            >
              <option value="">{subcategoryOptions.length ? "Not assigned" : "Create a category & subcategory first"}</option>
              {subcategoryOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Input>
            <small className="text-muted">
              The same categories as learning paths (e.g. Coding → Python). A hidden category hides its tests.
            </small>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Display order</Label>
            <Input type="number" min={0} max={100000} value={form.sortOrder} onChange={set("sortOrder")} disabled={disabled} />
            <small className="text-muted">Lower numbers show first to learners.</small>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <FormGroup>
            <Label>Time limit (minutes)</Label>
            <Input type="number" min={1} max={600} value={form.timeLimitMinutes} onChange={set("timeLimitMinutes")} placeholder="No limit" disabled={disabled} />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Pass mark (%)</Label>
            <Input type="number" min={0} max={100} value={form.passMark} onChange={set("passMark")} disabled={disabled} />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Max attempts</Label>
            <Input type="number" min={1} max={100} value={form.maxAttempts} onChange={set("maxAttempts")} placeholder="Unlimited" disabled={disabled} />
          </FormGroup>
        </Col>
      </Row>
      <CheckboxField
        className="mb-2"
        checked={form.instantFeedback}
        onChange={(v) => setValue("instantFeedback", v)}
        disabled={disabled}
        label="Learning mode: show right or wrong and the explanation after each card"
        hint="Turn off for exam mode: answers are revealed only on the result page."
      />
      <CheckboxField
        checked={form.shuffleQuestions}
        onChange={(v) => setValue("shuffleQuestions", v)}
        disabled={disabled}
        label="Shuffle the question order for each attempt"
      />
      <h6 className="mt-4 mb-1">What to recommend next</h6>
      <p className="small text-muted mb-2">
        Shown on the result page, next to the grade message. A pass is a score at or above the pass mark.
      </p>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>After a pass</Label>
            <Input type="select" value={form.passRecommendKind} onChange={set("passRecommendKind")} disabled={disabled}>
              <option value="">Nothing</option>
              <option value="course">A course</option>
              <option value="test">Another test</option>
            </Input>
          </FormGroup>
          {form.passRecommendKind === "course" && (
            <FormGroup>
              <Input
                type="select"
                value={form.passRecommendCurriculumId}
                onChange={set("passRecommendCurriculumId")}
                disabled={disabled}
                aria-label="Course to recommend after a pass"
              >
                <option value="">Choose a course</option>
                <CourseOptions curricula={curricula} />
              </Input>
            </FormGroup>
          )}
          {form.passRecommendKind === "test" && (
            <FormGroup>
              <Input
                type="select"
                value={form.passRecommendTestId}
                onChange={set("passRecommendTestId")}
                disabled={disabled}
                aria-label="Test to recommend after a pass"
              >
                <option value="">Choose a test</option>
                {otherTests.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                    {t.isPublished ? "" : " — draft"}
                  </option>
                ))}
              </Input>
              <small className="text-muted">A draft test is only suggested once it is published.</small>
            </FormGroup>
          )}
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>After a fail (course only)</Label>
            <Input type="select" value={form.failRecommendCurriculumId} onChange={set("failRecommendCurriculumId")} disabled={disabled}>
              <option value="">Nothing</option>
              <CourseOptions curricula={curricula} />
            </Input>
          </FormGroup>
        </Col>
      </Row>
    </>
  )
}

export default TestSettingsFields
